import { Users, Calendar, Award, Heart } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from './ui/card';
import { ImageWithFallback } from './figma/ImageWithFallback';

export function AboutPage() {
  const stats = [
    { icon: Users, value: '5000+', label: 'Happy Customers' },
    { icon: Calendar, value: '8+', label: 'Years Experience' },
    { icon: Award, value: '100%', label: 'Satisfaction Rate' },
    { icon: Heart, value: '24/7', label: 'Support Available' },
  ];

  const values = [
    {
      title: 'Safety First',
      description:
          'We prioritize the safety of your family, pets, and the environment in every treatment we provide. All products are Health Canada–approved.',
    },
    {
      title: 'Professional Excellence',
      description:
          'Our licensed and insured technicians bring over 8 years of Saskatchewan-specific pest control expertise and undergo continuous training to stay current.',
    },
    {
      title: 'Customer Satisfaction',
      description:
          'We back our services with a 100% satisfaction guarantee, ensuring your complete peace of mind.',
    },
    {
      title: 'Eco-Friendly Solutions',
      description:
          'We use environmentally responsible pest management techniques and preventive measures whenever possible.',
    },
  ];

  return (
      <div className="min-h-screen">
        <Helmet>
          <title>About BugBuddies Pest Solutions</title>
          <meta name="description" content="Learn about BugBuddies Pest Solutions' commitment to safe, professional, and eco-friendly pest control across Saskatchewan." />
          <link rel="canonical" href="https://bugbuddies.ca/about" />
        </Helmet>
        {/* Hero Section */}
        <section className="bg-gray-50 py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                  About BugBuddies Pest Solutions
                </h1>
                <p className="text-lg text-gray-600 mb-6">
                  BugBuddies Pest Solutions is committed to delivering professional, safe, and
                  environmentally responsible pest management services tailored to residential,
                  commercial, and industrial properties across Saskatchewan.
                </p>
                <p className="text-lg text-gray-600 mb-6">
                  Our team includes certified technicians with over 8 years of hands-on
                  experience in dealing with Saskatchewan-specific pest trends. From rodents and
                  insects to seasonal pests and pigeons, we offer reliable solutions backed by clear
                  reporting and prompt, flexible scheduling.
                </p>
                <p className="text-lg text-gray-600">
                  We proudly service well-known residential property management companies and are
                  dedicated to building long-term relationships with our customers through honest
                  communication, competitive pricing, and guaranteed results.
                </p>
              </div>
              <div className="relative">
                <ImageWithFallback
                    src="https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"
                    alt="BugBuddies team"
                    className="rounded-lg shadow-lg w-full h-96 object-cover"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <stat.icon className="h-10 w-10 text-[#075123] mx-auto mb-4" />
                    <div className="text-3xl font-bold text-gray-900 mb-2">
                      {stat.value}
                    </div>
                    <div className="text-gray-600">{stat.label}</div>
                  </div>
              ))}
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Our Values
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                These core values guide everything we do and shape our commitment to excellence.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {values.map((value, index) => (
                  <Card key={index} className="p-6 h-full">
                    <CardContent className="pt-0">
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">
                        {value.title}
                      </h3>
                      <p className="text-gray-600">{value.description}</p>
                    </CardContent>
                  </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16 bg-[#075123] text-white">
          <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Our Mission
            </h2>
            <p className="text-xl leading-relaxed">
              To provide superior pest control services that protect homes and businesses while
              maintaining the highest standards of safety and environmental responsibility. We aim to
              eliminate current infestations, prevent future problems, and deliver clear, detailed
              service reports—backed by our 100% satisfaction guarantee.
            </p>
          </div>
        </section>
      </div>
  );
}
